import React from 'react';
import { Spinner } from '@oysterjs/ui/spinner';
import { DataTable } from '@oysterjs/ui/data-table';
import { SearchFilterInput, useFiltersFromLocalStorage } from '@oysterjs/ui/search-filter';
import { MagnifyingGlassIcon, SquaresPlusIcon } from '@heroicons/react/24/outline';
import { getExecutionColumns, executionSortFn, type ExecutionSortKey } from './executions-columns';
import {
  createExecutionFilterDefinitions,
  filterExecutions,
  type ExecutionFilter
} from './executions-filters';
import { useGetWorkflowExecutionsQuery } from '~/api/graphql';
import { client } from '~/api/client';

const EmptyState = (props: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement> & { className?: string }>;
  title: string;
  description: string;
}) => (
  <div className="rounded-lg bg-white shadow px-4 sm:px-6 py-12 text-center dark:bg-neutral-800">
    <props.icon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
    <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">{props.title}</h3>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">{props.description}</p>
  </div>
);

export const WorkflowExecutionsPage = () => {
  const filterDefinitions = React.useMemo(() => createExecutionFilterDefinitions(), []);
  const { activeFilters, setActiveFilters, initialFilters, sortBy, sortDirection, setSort } =
    useFiltersFromLocalStorage<ExecutionFilter, ExecutionSortKey>(
      'workflow_executions_default_view',
      filterDefinitions,
      { defaultSortDirection: 'desc' }
    );

  const { data, loading } = useGetWorkflowExecutionsQuery({ client, pollInterval: 10000 });
  const executions = data?.workflowExecutions ?? [];
  const columns = React.useMemo(() => getExecutionColumns(), []);
  const filtered = filterExecutions(executions, activeFilters);

  return (
    <div className="space-y-4">
      <SearchFilterInput
        filterDefinitions={filterDefinitions}
        onFiltersChange={setActiveFilters}
        initialFilters={initialFilters}
        placeholder="Filter by ID, state, or date..."
      />

      {loading && executions.length === 0 ? (
        <div className="rounded-lg bg-white shadow px-6 dark:bg-neutral-800">
          <div className="text-center py-12">
            <Spinner size="size-8" color="neutral" />
            <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
              Loading executions...
            </h3>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={activeFilters.length > 0 ? MagnifyingGlassIcon : SquaresPlusIcon}
          title="No executions found"
          description={
            activeFilters.length > 0
              ? 'Try adjusting your search or filters.'
              : 'Start a workflow from the Gallery tab to see it here.'
          }
        />
      ) : (
        <div className="px-4 sm:px-6 rounded-lg bg-white shadow dark:bg-neutral-800">
          <DataTable
            data={filtered}
            columns={columns}
            keyExtractor={(exec) => exec.id}
            rowHref={(exec) => `/app/workflows/executions/${exec.id}`}
            tableClassName="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
            sortKey={sortBy}
            sortDirection={sortDirection}
            onSort={setSort}
            sortFn={executionSortFn}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};
